import { type V2, dirFromAngle, dist, lerp } from '../../core/math';
import { Rng } from '../../core/rng';
import type { AccountSave } from '../../game/account';
import type { Progression } from '../../game/Progression';
import type { ZoneRuntime, ZoneScript } from '../../game/ZoneRuntime';
import type { Actor } from '../../sim/actor';
import type { SimEvent } from '../../sim/events';
import type { ResolvedSkill } from '../../sim/skillTypes';
import { cleanse, removeStatus } from '../../sim/statusSystem';
import type { ActionKey } from '../../sim/types';
import type { InteractableDef } from '../../zones/types';
import { PATCH_DURATION, SHIP_BAR, SHIP_KEYS, SHIP_SKILL_DEFS, registerShipContent, resolveShipSkills } from './content';
import {
  BOW_REACH,
  BUMP_DAMAGE,
  BUMP_SPEED,
  CANNON_RANGE,
  FLOTSAM_HULL,
  FULL_SAIL_SPEED,
  FULL_SAIL_STATUS,
  KNOTS,
  LAST_PORT_FLAG,
  PATCH_FRACTION,
  PATCH_STATUS,
  PORTS,
  PORT_ARRIVAL,
  SALVAGE_RANGE,
  SHIP_MODEL,
  SHIP_NAME,
  SHIP_RADIUS,
  SKILL_CANNON,
  SKILL_PATCH,
  TOW_DELAY,
  WEAR_PER_METRE,
  WRECK_SITES,
  type WreckSite,
  isPort,
} from './data';
import {
  HAZARDS,
  type HazardSample,
  type HazardWarning,
  LightningClock,
  hazardWarning,
  lightningDamage,
  lightningHits,
  sampleHazards,
  stormCurrent,
  stormDamage,
} from './hazards';
import { type ShipMotion, type ShipSave, type ShipStats, bearingDeg, damageHull, headingDeg, healHull, lastPort, newMotion, shipOf, statsOf, steer, towShip } from './ship';
import { cannonBlasts, markSalvaged, salvageLoot, wreckAvailable } from './wrecks';

/**
 * The sea zone script. On the Gleaming Sea the local player's actor *is* the
 * ship: its look becomes the hull (replicated like any look change), its bar
 * holds the ship's abilities, and clicks steer a turn-rate-limited helm
 * instead of walking. Hazards, hull wear, salvage and towing run here too —
 * all of it local to the captain (each peer sails its own ship; everyone sees
 * the others through the normal replicated movement and look).
 */

type AnnounceStyle = 'boss' | 'mechanic' | 'info' | 'warning' | 'success';

/** What sailing needs from the game (the `Game` satisfies it; tests pass a stub). */
export interface SailHost {
  readonly account: AccountSave;
  readonly progression: Progression | null;
  readonly player: Actor | null;
  travel(zone: string, spawn: string): void;
  readonly hud?: {
    toast(html: string, color?: string): void;
    announce(text: string, style?: AnnounceStyle): void;
    callout(text: string): void;
  } | null;
}

/** Presentation hooks (3D effects); null when headless. */
export interface SeaFx {
  wreckAdded(site: WreckSite): void;
  wreckRemoved(site: WreckSite, how: SalvageHow): void;
  lightning(at: V2, hit: boolean): void;
  aground(at: V2): void;
  dispose(): void;
}

export type SalvageHow = 'hull' | 'cannon' | 'interact';

interface SavedLoadout {
  look: Actor['look'];
  radius: number;
  bar: (string | null)[];
  awakening: string | null;
  skills: Map<string, ResolvedSkill>;
}

export interface LocalShip {
  actor: Actor;
  motion: ShipMotion;
  saved: SavedLoadout;
  /** Pending wear (hull) accumulated from distance sailed. */
  wear: number;
  bumpCd: number;
  lightning: LightningClock;
  towing: { t: number; port: string; free: boolean; done: boolean } | null;
  /** Metres sailed and not yet logged to the `sailMetres` counter. */
  sailed: number;
  cannonCdr: number;
}

export interface HudMarker {
  kind: 'port' | 'storm' | 'fog' | 'wreck';
  label: string;
  bearing: number;
  distance: number;
}

export interface HudSkill {
  id: string;
  key: string;
  name: string;
  cd: number;
  cdMax: number;
  active: boolean;
}

/** Everything the sea HUD shows (plain data, refreshed a few times a second). */
export interface SeaHudState {
  shipName: string;
  level: number;
  hull: number;
  maxHull: number;
  /** Hull lost per second right now (smoothed). */
  hullRate: number;
  knots: number;
  maxKnots: number;
  heading: number;
  fullSail: number;
  patching: boolean;
  storm: number;
  fog: number;
  warning: HazardWarning | null;
  markers: HudMarker[];
  skills: HudSkill[];
  towing: { port: string; free: boolean } | null;
  salvageHint: { name: string; distance: number } | null;
}

/** Runtimes by zone (at most one live sea at a time; the system and UI read `activeSea`). */
let active: SeaRuntime | null = null;
export const activeSea = (): SeaRuntime | null => (active && !active.disposed ? active : null);

/**
 * Zone setup: harbours remember themselves as the tow destination; on the sea
 * the local player boards the ship and the sea script is attached.
 */
export function setupSailingZone(host: SailHost, z: ZoneRuntime): SeaRuntime | null {
  const prog = host.progression;
  if (prog && isPort(z.layout.id)) prog.char.flags[LAST_PORT_FLAG] = z.layout.id;
  if (z.layout.kind !== 'sea') return null;
  const rt = new SeaRuntime(host, z);
  z.scripts.push(rt);
  active = rt;
  return rt;
}

export class SeaRuntime implements ZoneScript {
  ship: LocalShip | null = null;
  fx: SeaFx | null = null;
  /** Wrecks currently afloat for this captain. */
  readonly wrecks = new Map<string, WreckSite>();
  hazards: HazardSample = { storm: 0, fog: 0, stormHazard: null, fogHazard: null };
  /** Smoothed hull loss per second (HUD). */
  hullRate = 0;
  seaLevel = 0;
  disposed = false;
  time = 0;
  /** Travel requested during a sim step (the tug), performed by `flush`. */
  pendingTravel: { zone: string; spawn: string } | null = null;
  private lastHull = -1;
  private readonly rng: Rng;
  private pendingBlasts: { id: string; at: number }[] = [];
  private respawnT = 0;
  private inStorm = false;
  private inFog = false;

  constructor(
    readonly host: SailHost,
    readonly z: ZoneRuntime,
  ) {
    registerShipContent();
    this.rng = new Rng(`sea:${z.opts.seed}`);
    this.floatOnWater();
    this.patchWorld();
    this.board();
    const char = host.progression?.char;
    for (const site of WRECK_SITES) if (wreckAvailable(char, site)) this.addWreck(site);
  }

  // ---------------------------------------------------------------- engine glue

  /**
   * Heights clamp to the water sheet: ships, markers, cursor picks, VFX and
   * the camera all ride the surface instead of the sea floor far below.
   */
  private floatOnWater(): void {
    const layout = this.z.layout;
    this.seaLevel = layout.water.find((w) => w.shape.kind === 'sheet')?.level ?? 0;
    const floor = layout.heightAt;
    const level = this.seaLevel;
    this.z.heightAt = (x, z) => Math.max(floor(x, z), level);
  }

  /**
   * Routes the local ship's input through the helm: `moveTo` (clicks and held
   * mouse) sets the steering target instead of pathing, `pressKey` casts ship
   * abilities only (no class skills, dash, identity or awakening at sea).
   * Other actors keep the engine's behaviour.
   */
  private patchWorld(): void {
    const w = this.z.world;
    const moveTo = w.moveTo.bind(w);
    const pressKey = w.pressKey.bind(w);
    const stop = w.stop.bind(w);
    w.moveTo = (a, goal) => {
      const s = this.localShip(a);
      if (s) this.steerTo(s, goal);
      else moveTo(a, goal);
    };
    w.pressKey = (a, key, aim) => {
      const s = this.localShip(a);
      return s ? this.helmKey(s, key, aim) : pressKey(a, key, aim);
    };
    w.stop = (a) => {
      const s = this.localShip(a);
      if (s) s.motion.target = null;
      stop(a);
    };
  }

  private localShip(a: Actor): LocalShip | null {
    const s = this.ship;
    return s && s.actor === a && !this.disposed ? s : null;
  }

  // ---------------------------------------------------------------- boarding

  /** Turns the local player's actor into the ship (look, body, skill bar). */
  board(): void {
    const a = this.host.player;
    const w = this.z.world;
    if (!a?.player || w.actors.get(a.id) !== a || !w.isLocal(a) || this.ship?.actor === a) return;
    if (this.ship) this.disembark();
    const pl = a.player;
    const saved: SavedLoadout = { look: { ...a.look }, radius: a.radius, bar: [...pl.bar], awakening: pl.awakening, skills: new Map(a.skills) };
    if (a.cast) w.endCast(a, true);
    a.path = [];
    a.moveGoal = null;
    a.moveDir = null;
    a.displacement = null;
    cleanse(w, a);
    this.ship = { actor: a, motion: newMotion(a.facing), saved, wear: 0, bumpCd: 0, lightning: new LightningClock(this.rng), towing: null, sailed: 0, cannonCdr: -1 };
    this.applyLoadout(this.ship);
    const ship = shipOf(this.host.account);
    this.lastHull = ship.hull;
    if (ship.hull <= 0) this.startTow(this.ship, true);
  }

  private applyLoadout(s: LocalShip): void {
    const a = s.actor;
    const pl = a.player;
    if (!pl) return;
    const stats = statsOf(shipOf(this.host.account));
    if (a.look.model !== SHIP_MODEL) {
      a.look = { model: SHIP_MODEL };
      this.z.world.emit({ type: 'look', id: a.id, model: SHIP_MODEL });
    }
    a.radius = SHIP_RADIUS;
    pl.bar = [...SHIP_BAR];
    pl.awakening = null;
    a.skills.clear();
    for (const sk of resolveShipSkills(stats)) a.skills.set(sk.id, sk);
    s.cannonCdr = stats.cannonCdr;
  }

  /**
   * Keeps the ship loadout in place: `Game.refreshPlayer()` (level-ups, gear)
   * rebuilds the class bar — remember that one for disembarking and put the
   * ship's back.
   */
  private ensureLoadout(s: LocalShip, stats: ShipStats): void {
    const a = s.actor;
    const pl = a.player;
    if (!pl) return;
    if (pl.bar[0] !== SHIP_BAR[0] || !a.skills.has(SKILL_CANNON) || a.skills.size !== SHIP_SKILL_DEFS.length) {
      s.saved = { ...s.saved, bar: [...pl.bar], awakening: pl.awakening, skills: new Map(a.skills) };
      this.applyLoadout(s);
    } else if (a.look.model !== SHIP_MODEL || s.cannonCdr !== stats.cannonCdr) this.applyLoadout(s);
    a.radius = SHIP_RADIUS;
  }

  /** Restores the character (look, body, class bar). Docking recreates the actor anyway. */
  disembark(): void {
    const s = this.ship;
    if (!s) return;
    this.ship = null;
    const a = s.actor;
    a.look = { ...s.saved.look };
    if (this.z.world.actors.get(a.id) === a) this.z.world.emit({ type: 'look', id: a.id, model: a.look.model });
    a.radius = s.saved.radius;
    if (a.player) {
      a.player.bar = [...s.saved.bar];
      a.player.awakening = s.saved.awakening;
    }
    a.skills.clear();
    for (const [id, sk] of s.saved.skills) a.skills.set(id, sk);
    for (const id of [FULL_SAIL_STATUS, PATCH_STATUS]) if (a.hasStatus(id)) removeStatus(this.z.world, a, id);
    a.path = [];
    a.moveGoal = null;
    a.speed = 0;
  }

  // ---------------------------------------------------------------- helm input

  private steerTo(s: LocalShip, goal: V2): void {
    if (s.towing) return;
    s.motion.target = this.z.nav.clampToBounds(goal);
  }

  private helmKey(s: LocalShip, key: ActionKey, aim?: V2): boolean {
    const id = SHIP_KEYS[key];
    const a = s.actor;
    if (!id || s.towing || !a.alive) return false;
    const skill = a.skills.get(id);
    if (!skill) return false;
    if (id === SKILL_PATCH) {
      const ship = shipOf(this.host.account);
      if (ship.hull >= statsOf(ship).maxHull - 0.5) {
        if ((a.cooldowns.get(id) ?? 0) <= 0) this.host.hud?.toast('The hull is already sound.', '#9be07a');
        return false;
      }
    }
    const w = this.z.world;
    const ahead = dirFromAngle(s.motion.heading);
    const point = aim ?? w.aimOf(a) ?? { x: a.pos.x + ahead.x * 10, z: a.pos.z + ahead.z * 10 };
    const ok = w.useSkill(a, skill, key, point);
    // Casting snaps facing to the aim; the hull keeps its heading (the volley still flies at the cursor).
    a.facing = s.motion.heading;
    return ok;
  }

  // ---------------------------------------------------------------- simulation

  tick(_z: ZoneRuntime, dt: number): void {
    if (this.disposed) return;
    this.time += dt;
    const w = this.z.world;
    // Every hull in the zone gets a hull-sized body (remote ships too: this only affects local separation).
    for (const a of w.actors.values()) if (a.kind === 'player' && a.look.model === SHIP_MODEL) a.radius = SHIP_RADIUS;
    if (this.ship?.actor !== this.host.player) this.board();
    const s = this.ship;
    if (!s || w.actors.get(s.actor.id) !== s.actor) return;
    const ship = shipOf(this.host.account);
    const stats = statsOf(ship);
    this.ensureLoadout(s, stats);
    this.runBlasts();
    this.respawnWrecks(dt);
    if (!s.actor.alive) return;
    this.sail(s, ship, stats, dt);
    this.weather(s, ship, stats, dt);
    this.salvageNearby(s);
    this.checkHull(s, ship, dt);
  }

  private sail(s: LocalShip, ship: ShipSave, stats: ShipStats, dt: number): void {
    const a = s.actor;
    const m = s.motion;
    const nav = this.z.nav;
    const hz = sampleHazards(a.pos);
    this.hazards = hz;
    if (s.towing) m.target = null;
    const fullSail = a.hasStatus(FULL_SAIL_STATUS);
    const delta = steer(m, a.pos, stats, dt, {
      speedMult: (fullSail ? FULL_SAIL_SPEED : 1) * (1 - 0.15 * hz.storm),
      accelMult: fullSail ? 2 : 1,
      drag: s.towing ? 3 : 1,
    });
    // Storm currents shove the hull about (a sturdier ship and a bosun resist them).
    if (hz.stormHazard && hz.storm > 0) {
      const c = stormCurrent(hz.stormHazard, a.pos, hz.storm);
      const k = (1 - stats.stormResist) * dt;
      delta.x += c.x * k;
      delta.z += c.z * k;
    }
    // Land ahead: the bow probe stops forward way (and scrapes the hull when fast).
    const fwd = dirFromAngle(m.heading);
    const bow = { x: a.pos.x + delta.x + fwd.x * BOW_REACH, z: a.pos.z + delta.z + fwd.z * BOW_REACH };
    s.bumpCd = Math.max(0, s.bumpCd - dt);
    if (m.speed > 0.05 && !nav.walkable(bow)) {
      if (m.speed > BUMP_SPEED && s.bumpCd <= 0) {
        s.bumpCd = 1.2;
        damageHull(ship, (m.speed - BUMP_SPEED) * BUMP_DAMAGE * 2.5);
        this.host.hud?.callout('Ran aground!');
        this.fx?.aground(bow);
      }
      const along = delta.x * fwd.x + delta.z * fwd.z;
      if (along > 0) {
        delta.x -= fwd.x * along;
        delta.z -= fwd.z * along;
      }
      m.speed = Math.min(m.speed, 0.6);
    }
    const want = Math.hypot(delta.x, delta.z);
    const before = { x: a.pos.x, z: a.pos.z };
    a.pos = nav.slide(a.pos, delta);
    const moved = dist(before, a.pos);
    // Pinned against the shore: lose way instead of showing phantom speed.
    if (want > 1e-4 && moved < want * 0.3) m.speed = Math.min(m.speed, moved / dt);
    // The engine's walking movement stays idle: the helm owns position and facing.
    a.facing = m.heading;
    a.speed = moved / dt;
    a.path = [];
    a.moveGoal = null;
    a.moveDir = null;
    s.sailed += moved;
    const prog = this.host.progression;
    if (prog && s.sailed >= 10) {
      const whole = Math.floor(s.sailed);
      prog.char.counters.sailMetres = (prog.char.counters.sailMetres ?? 0) + whole;
      s.sailed -= whole;
    }
    s.wear += moved * WEAR_PER_METRE;
    if (s.wear >= 0.25) {
      damageHull(ship, s.wear);
      s.wear = 0;
    }
  }

  private weather(s: LocalShip, ship: ShipSave, stats: ShipStats, dt: number): void {
    const a = s.actor;
    const hz = this.hazards;
    const hud = this.host.hud;
    // Under tow the harbour crew has the ship: no further battering.
    if (s.towing) return;
    if (hz.storm > 0) damageHull(ship, stormDamage(hz.storm, stats.stormResist, dt));
    const strike = s.lightning.update(dt, hz.storm, a.pos);
    if (strike) {
      const hit = lightningHits(strike, a.pos);
      if (hit) {
        damageHull(ship, lightningDamage(stats.stormResist));
        hud?.callout('Lightning strike!');
      }
      this.fx?.lightning(strike, hit);
    }
    if (a.hasStatus(PATCH_STATUS)) healHull(ship, ((stats.maxHull * PATCH_FRACTION) / PATCH_DURATION) * dt, stats);
    const storm = hz.storm > 0.05;
    const fog = hz.fog > 0.15;
    if (storm && !this.inStorm) hud?.announce('Storm! The hull takes a beating — sail clear or ride it out.', 'warning');
    if (fog && !this.inFog && !storm) hud?.announce('A fog bank closes in.', 'info');
    this.inStorm = storm;
    this.inFog = fog;
  }

  private checkHull(s: LocalShip, ship: ShipSave, dt: number): void {
    if (this.lastHull >= 0) this.hullRate = lerp(this.hullRate, Math.max(0, this.lastHull - ship.hull) / dt, Math.min(1, dt * 3));
    this.lastHull = ship.hull;
    if (!s.towing && ship.hull <= 0) this.startTow(s, false);
    const t = s.towing;
    if (t && !t.done) {
      t.t -= dt;
      if (t.t <= 0) this.finishTow(s);
    }
  }

  // ---------------------------------------------------------------- towing

  private startTow(s: LocalShip, free: boolean): void {
    const port = lastPort(this.host.progression?.char);
    s.towing = { t: free ? 1.2 : TOW_DELAY, port, free, done: false };
    s.motion.target = null;
    const name = PORTS[port]?.name ?? port;
    this.host.hud?.announce(free ? `${SHIP_NAME} isn't seaworthy — back to ${name} for repairs.` : 'The hull is breached! A harbour tug is on its way.', 'warning');
  }

  private finishTow(s: LocalShip): void {
    const t = s.towing;
    if (!t || t.done) return;
    t.done = true;
    const res = towShip(this.host.account, t.free);
    const name = PORTS[t.port]?.name ?? t.port;
    if (!t.free) {
      this.host.hud?.toast(`Towed to ${name}. Tug and emergency repairs: ${res.fee.toLocaleString('en-US')} silver.`, '#f2b33d');
      this.host.progression?.emit('wallet', { silver: this.host.account.wallet.silver, gold: this.host.account.wallet.gold });
    }
    // Travelling disposes this zone: leave that to the frame loop (`flush`), never mid-step.
    this.pendingTravel = { zone: t.port, spawn: PORT_ARRIVAL };
  }

  /** Performs travel requested during the simulation step (called once per frame, like portal checks). */
  flush(): void {
    const t = this.pendingTravel;
    if (!t || this.disposed) return;
    this.pendingTravel = null;
    this.host.travel(t.zone, t.spawn);
  }

  // ---------------------------------------------------------------- salvage

  private addWreck(site: WreckSite): void {
    if (this.wrecks.has(site.id)) return;
    this.wrecks.set(site.id, site);
    if (!this.z.interactables.some((i) => i.id === site.id)) {
      // A 'chest' interactable: minimap icon and G-to-open; the sailing handler salvages it.
      const it: InteractableDef = { id: site.id, kind: 'chest', x: site.x, z: site.z, data: { wreck: site.kind, name: site.name } };
      this.z.interactables.push(it);
      if (!this.z.opts.headless) this.z.addInteractMarker(it);
    }
    this.fx?.wreckAdded(site);
  }

  private removeWreck(site: WreckSite, how: SalvageHow): void {
    if (!this.wrecks.delete(site.id)) return;
    this.z.removeInteractable(site.id);
    this.pendingBlasts = this.pendingBlasts.filter((b) => b.id !== site.id);
    this.fx?.wreckRemoved(site, how);
  }

  private respawnWrecks(dt: number): void {
    this.respawnT -= dt;
    if (this.respawnT > 0) return;
    this.respawnT = 2;
    const char = this.host.progression?.char;
    for (const site of WRECK_SITES) if (!this.wrecks.has(site.id) && wreckAvailable(char, site)) this.addWreck(site);
  }

  private salvageNearby(s: LocalShip): void {
    if (s.towing) return;
    for (const site of this.wrecks.values()) if (dist(s.actor.pos, site) <= SALVAGE_RANGE) this.salvage(site.id, 'hull');
  }

  private runBlasts(): void {
    if (this.pendingBlasts.length === 0) return;
    const now = this.z.world.time;
    const due = this.pendingBlasts.filter((b) => b.at <= now);
    if (due.length === 0) return;
    this.pendingBlasts = this.pendingBlasts.filter((b) => b.at > now);
    for (const b of due) this.salvage(b.id, 'cannon');
  }

  /** Plunders a wreck for this captain: loot, a little hull from flotsam timber, counters. */
  salvage(id: string, how: SalvageHow): boolean {
    const site = this.wrecks.get(id);
    const prog = this.host.progression;
    if (!site || !prog) return false;
    const ship = shipOf(this.host.account);
    const stats = statsOf(ship);
    const drop = salvageLoot(site.kind, prog.char.level, prog.rng, stats.salvageBonus);
    markSalvaged(prog.char, site);
    this.removeWreck(site, how);
    prog.grantDrop(drop, site.name);
    if (site.kind === 'flotsam') healHull(ship, FLOTSAM_HULL, stats);
    prog.char.counters.wrecksSalvaged = (prog.char.counters.wrecksSalvaged ?? 0) + 1;
    prog.questEvent({ type: 'interact', target: 'sea_wreck' });
    const gold = drop.gold ? ` · ${drop.gold} gold` : '';
    this.host.hud?.toast(`${how === 'cannon' ? 'Blasted open' : 'Salvaged'} ${site.name}: ${drop.silver.toLocaleString('en-US')} silver${gold}`, '#ffd070');
    return true;
  }

  // ---------------------------------------------------------------- events

  event(_z: ZoneRuntime, ev: SimEvent): void {
    const s = this.ship;
    // Our cannonballs knock wreck cargo loose when they pass close enough.
    if (ev.type !== 'projectile' || !s || ev.ownerId !== s.actor.id) return;
    const hit = cannonBlasts(ev.pos, ev.dir, CANNON_RANGE, ev.speed, [...this.wrecks.values()])[0];
    if (hit && !this.pendingBlasts.some((b) => b.id === hit.site.id)) this.pendingBlasts.push({ id: hit.site.id, at: this.z.world.time + hit.delay });
  }

  dispose(): void {
    if (this.disposed) return;
    if (this.ship) this.disembark();
    this.disposed = true;
    this.fx?.dispose();
    this.fx = null;
    if (active === this) active = null;
  }

  // ---------------------------------------------------------------- HUD

  hudState(): SeaHudState | null {
    const s = this.ship;
    if (!s) return null;
    const a = s.actor;
    const ship = shipOf(this.host.account);
    const stats = statsOf(ship);
    const p = a.pos;
    const markers: HudMarker[] = [];
    for (const portal of this.z.layout.portals) {
      if (portal.kind !== 'dock') continue;
      markers.push({ kind: 'port', label: PORTS[portal.to]?.name ?? portal.label, bearing: bearingDeg(portal.x - p.x, portal.z - p.z), distance: dist(p, portal) });
    }
    for (const h of HAZARDS) {
      const d = Math.max(0, dist(p, h) - h.r);
      if (d < 240) markers.push({ kind: h.kind, label: h.kind === 'storm' ? 'Storm' : 'Fog bank', bearing: bearingDeg(h.x - p.x, h.z - p.z), distance: d });
    }
    let near: WreckSite | null = null;
    let nearD = 170;
    for (const w of this.wrecks.values()) {
      const d = dist(p, w);
      if (d < nearD) {
        near = w;
        nearD = d;
      }
    }
    if (near) markers.push({ kind: 'wreck', label: near.name, bearing: bearingDeg(near.x - p.x, near.z - p.z), distance: nearD });
    const skills: HudSkill[] = [];
    SHIP_BAR.forEach((id, i) => {
      const sk = id ? a.skills.get(id) : undefined;
      if (!sk) return;
      skills.push({ id: sk.id, key: 'QWERASDF'[i] ?? '', name: sk.name, cd: a.cooldowns.get(sk.id) ?? 0, cdMax: sk.cooldown * (1 - a.stats.cdr), active: sk.id === SKILL_PATCH ? a.hasStatus(PATCH_STATUS) : a.hasStatus(FULL_SAIL_STATUS) && sk.id === SHIP_BAR[0] });
    });
    const fullSail = a.getStatus(FULL_SAIL_STATUS)?.remaining ?? 0;
    return {
      shipName: SHIP_NAME,
      level: ship.level,
      hull: ship.hull,
      maxHull: stats.maxHull,
      hullRate: this.hullRate,
      knots: s.motion.speed * KNOTS,
      maxKnots: stats.maxSpeed * KNOTS,
      heading: headingDeg(s.motion.heading),
      fullSail,
      patching: a.hasStatus(PATCH_STATUS),
      storm: this.hazards.storm,
      fog: this.hazards.fog,
      warning: hazardWarning(p, s.motion.heading),
      markers,
      skills,
      towing: s.towing ? { port: PORTS[s.towing.port]?.name ?? s.towing.port, free: s.towing.free } : null,
      salvageHint: near && nearD < 60 ? { name: near.name, distance: nearD } : null,
    };
  }
}
